import React, { useState, useEffect, useCallback } from 'react'
import { Drawer, Input, Select, Spin, message, Table, Tooltip, DatePicker as AntDatePicker } from 'antd'
import { DeleteOutlined, CloseOutlined, UndoOutlined, PlusOutlined, EditOutlined, SaveOutlined } from '@ant-design/icons'
import moment from 'moment'
import styled from 'styled-components'
import adminRequest from 'utils/adminRequest'
import Label from 'components/Form/Label'
import Button from 'components/Button'
import {
  countryCodeOptions,
  DEFAULT_COUNTRY_CODE,
  formatMobile,
  nationalNumberLength,
  parseCandidateMobile,
  splitFullMobile,
} from 'utils/candidateMobile'
import AssessmentConfigurationSection from './AssessmentConfigurationSection'
import { diffConfiguration } from './assessmentConfigDiff'
import { buildMobileUpdates } from './studentMobileEdits'

const DrawerContent = styled.div`
  padding: 24px;
`

const Section = styled.div`
  margin-bottom: 24px;
`

const SectionTitle = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: #1f2937;
  margin-bottom: 12px;
  padding-bottom: 8px;
  border-bottom: 1px solid #e5e7eb;
`

const InfoBadge = styled.div`
  display: inline-flex;
  align-items: center;
  padding: 4px 12px;
  border-radius: 4px;
  font-size: 13px;
  font-weight: 500;
  background: ${props => props.bg || '#f0f0f0'};
  color: ${props => props.color || '#333'};
`

const FooterContainer = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  padding: 16px 24px;
  border-top: 1px solid #e5e7eb;
`

const StudentCount = styled.span`
  font-size: 12px;
  color: #6b7280;
  margin-left: 8px;
`

const AddStudentForm = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-bottom: 12px;
`

const AddStudentRow = styled.div`
  display: flex;
  gap: 8px;
`

/* Country code is its own control so the number box is unambiguously the
   national number — mirrors the Add Candidate drawer's mobile field. */
const MobileFieldGroup = styled.div`
  display: flex;
  flex: 1;
  gap: 8px;

  .ant-select {
    flex: 0 0 108px;
  }

  .ant-input {
    flex: 1;
    min-width: 120px;
  }
`

/* The inline mobile editor for an existing row: a narrow dial-code Select next to
   the number, sized to sit inside the Mobile column without pushing the email out. */
const InlineMobileEditor = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;

  .ant-select {
    flex: 0 0 92px;
  }

  .ant-input {
    flex: 1;
    min-width: 96px;
  }
`

const ProfileTag = styled.span`
  display: inline-block;
  padding: 0 4px;
  border-radius: 3px;
  font-size: 10px;
  font-weight: 500;
  background: #f3f4f6;
  color: #6b7280;
  margin-left: 6px;
`

const NewBadge = styled.span`
  display: inline-flex;
  align-items: center;
  padding: 1px 8px;
  border-radius: 4px;
  font-size: 11px;
  font-weight: 500;
  background: #ECFDF5;
  color: #059669;
  margin-left: 8px;
`

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

const EditAssessmentDrawer = ({ visible, mapId, mapType, onClose, onSuccess }) => {
  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)

  // Editable fields
  const [name, setName] = useState('')
  const [endDate, setEndDate] = useState(null)
  const [students, setStudents] = useState([]) // existing: [{ assignedId, email, mobile, mobileFromProfile, attempted }]
  const [removeAssignedIds, setRemoveAssignedIds] = useState([]) // assignedIds toggled for removal
  // Mobile edits staged against existing rows: { [assignedId]: { countryCode, phone } }.
  // Diffed on save so an opened-but-untouched drawer sends nothing.
  const [mobileEdits, setMobileEdits] = useState({})
  const [editingMobileId, setEditingMobileId] = useState(null)
  const [editCountryCode, setEditCountryCode] = useState(DEFAULT_COUNTRY_CODE)
  const [editMobile, setEditMobile] = useState('')
  // new students staged: [{ firstName, lastName, email, countryCode, phone }]
  const [addStudents, setAddStudents] = useState([])
  const [newFirstName, setNewFirstName] = useState('')
  const [newLastName, setNewLastName] = useState('')
  const [newEmail, setNewEmail] = useState('')
  const [newCountryCode, setNewCountryCode] = useState(DEFAULT_COUNTRY_CODE)
  const [newMobile, setNewMobile] = useState('')

  // Type-specific configuration (job description, CEFR level, interview brief …)
  // as published, plus the admin's pending edits to the editable subset of it.
  const [configuration, setConfiguration] = useState(null)
  const [configEdits, setConfigEdits] = useState({})

  // Snapshot to diff on save
  const [original, setOriginal] = useState({ name: '', endTime: null })

  const fetchDetails = useCallback(async () => {
    if (!mapId) return
    setLoading(true)
    try {
      const response = await adminRequest.get('/assessment/details', {
        params: { mapId, mapType },
      })
      const data = response?.data?.data
      setName(data?.name || '')
      // Parse the stored timestamp in UTC, not browser-local. The backend stores
      // assessment end times as "IST wall-clock written as UTC" (e.g. 18 Jun is
      // saved as 2026-06-18T23:59:59Z). moment(...) would parse that in the
      // browser's IST and roll it to 19 Jun; moment.utc(...) keeps the intended date.
      setEndDate(data?.endTime ? moment.utc(data.endTime) : null)
      setStudents(Array.isArray(data?.students) ? data.students : [])
      setMobileEdits({})
      setEditingMobileId(null)
      setConfiguration(data?.configuration || null)
      setConfigEdits({})
      setOriginal({ name: data?.name || '', endTime: data?.endTime || null })
    } catch (error) {
      console.error('Error fetching assessment details:', error)
      message.error(error?.response?.data?.message || 'Failed to load assessment details')
    } finally {
      setLoading(false)
    }
  }, [mapId, mapType])

  useEffect(() => {
    if (visible && mapId) {
      fetchDetails()
    }
    if (!visible) {
      // Reset all state when drawer closes
      setName('')
      setEndDate(null)
      setStudents([])
      setRemoveAssignedIds([])
      setMobileEdits({})
      setEditingMobileId(null)
      setEditCountryCode(DEFAULT_COUNTRY_CODE)
      setEditMobile('')
      setAddStudents([])
      setNewFirstName('')
      setNewLastName('')
      setNewEmail('')
      // Reset to the default dial code, not '' — an empty value renders the
      // country-code Select blank.
      setNewCountryCode(DEFAULT_COUNTRY_CODE)
      setNewMobile('')
      setConfiguration(null)
      setConfigEdits({})
      setOriginal({ name: '', endTime: null })
    }
  }, [visible, mapId, fetchDetails])

  const handleConfigChange = useCallback((key, value) => {
    setConfigEdits(prev => ({ ...prev, [key]: value }))
  }, [])

  const toggleRemove = (record) => {
    // Attempted students can never be toggled for removal
    if (record.attempted) return
    setRemoveAssignedIds(prev =>
      prev.includes(record.assignedId)
        ? prev.filter(id => id !== record.assignedId)
        : [...prev, record.assignedId]
    )
  }

  // Mobile editing on an EXISTING row. Allowed even for attempted students —
  // unlike removal this is only contact data, and the /s/ invite resolves the
  // phone at click time, so a correction still reaches an already-sent link.
  const handleEditMobile = (record) => {
    const staged = mobileEdits[record.assignedId]
    const stored = staged || splitFullMobile(record.mobile)
    setEditingMobileId(record.assignedId)
    setEditCountryCode(stored?.countryCode || DEFAULT_COUNTRY_CODE)
    setEditMobile(staged ? staged.phone : (stored?.nationalNumber || ''))
  }

  const handleCancelEditMobile = () => {
    setEditingMobileId(null)
    setEditCountryCode(DEFAULT_COUNTRY_CODE)
    setEditMobile('')
  }

  const handleSaveMobile = (assignedId) => {
    const entered = editMobile.trim()
    // Blank is a deliberate CLEAR — it drops the assignment-level number so the
    // invite falls back to the student's profile.
    if (entered) {
      const parsed = parseCandidateMobile(editCountryCode, entered)
      if (!parsed) {
        message.warning(
          `Please enter a valid ${nationalNumberLength(editCountryCode)}-digit mobile number for ${editCountryCode} (without the country code)`
        )
        return
      }
      setMobileEdits(prev => ({
        ...prev,
        [assignedId]: { countryCode: parsed.countryCode, phone: parsed.nationalNumber },
      }))
    } else {
      setMobileEdits(prev => ({ ...prev, [assignedId]: { countryCode: editCountryCode, phone: '' } }))
    }
    handleCancelEditMobile()
  }

  const handleAddStudent = () => {
    const firstName = newFirstName.trim()
    const lastName = newLastName.trim()
    const email = newEmail.trim()
    // First name is required so brand-new students can have an account created
    // (the platform rejects account creation without a name); last name is optional.
    if (!firstName) {
      message.warning('Please enter the first name')
      return
    }
    if (!email) {
      message.warning('Please enter an email')
      return
    }
    if (!EMAIL_REGEX.test(email)) {
      message.warning('Please enter a valid email address')
      return
    }
    // Mobile is optional, but a number that is entered must be valid — a mangled
    // number is worse than none, since the SMS OTP would silently go nowhere.
    const enteredMobile = newMobile.trim()
    const mobile = parseCandidateMobile(newCountryCode, enteredMobile)
    if (enteredMobile && !mobile) {
      message.warning(
        `Please enter a valid ${nationalNumberLength(newCountryCode)}-digit mobile number for ${newCountryCode} (without the country code)`
      )
      return
    }
    const lower = email.toLowerCase()
    const existingEmails = students.map(s => (s.email || '').toLowerCase())
    const stagedEmails = addStudents.map(s => s.email.toLowerCase())
    if (existingEmails.includes(lower) || stagedEmails.includes(lower)) {
      message.warning('This student is already in the list')
      return
    }
    setAddStudents(prev => [
      ...prev,
      {
        firstName,
        lastName,
        email,
        countryCode: mobile?.countryCode || newCountryCode,
        phone: mobile?.nationalNumber || '',
      },
    ])
    setNewFirstName('')
    setNewLastName('')
    setNewEmail('')
    // Keep the chosen country code — students are added in batches from the same
    // region, so resetting it to +91 every time would be a nuisance.
    setNewMobile('')
  }

  const handleRemoveStagedStudent = (email) => {
    setAddStudents(prev => prev.filter(s => s.email !== email))
  }

  const handleSave = async () => {
    if (!name.trim()) {
      message.warning('Assessment name is required')
      return
    }
    if (!endDate) {
      message.warning('End date is required')
      return
    }

    const payload = { mapId, mapType }
    if (name.trim() !== original.name) payload.name = name.trim()
    if (endDate && (!original.endTime || !endDate.isSame(moment.utc(original.endTime), 'minute'))) {
      // Send the wall-clock date+time (no timezone). The backend stores these exact
      // numbers as UTC, matching the read-side which renders endTime via moment.utc.
      payload.endTime = endDate.format('YYYY-MM-DDTHH:mm:ss')
    }
    if (removeAssignedIds.length) payload.removeAssignedIds = removeAssignedIds
    // Only rows whose number actually differs from what was loaded. Rows staged
    // for removal are excluded: they are deleted first, so sending their edit
    // would come back as a pointless "not part of this assessment" warning.
    const mobileUpdates = buildMobileUpdates(
      mobileEdits,
      students.filter(s => !removeAssignedIds.includes(s.assignedId))
    )
    if (mobileUpdates.length) payload.updateStudents = mobileUpdates
    if (addStudents.length) {
      // `mobile_number` + `country_code` are the key spellings admin-node's
      // candidateMobile helper reads; it stores the E.164 form on the assignment
      // row's contact_number, which is what the /s/ invite resolver uses for the
      // SMS OTP. Omitted entirely when blank so no empty string reaches the API.
      payload.addStudents = addStudents.map(s => ({
        firstName: s.firstName,
        lastName: s.lastName,
        email: s.email,
        ...(s.phone ? { mobile_number: s.phone, country_code: s.countryCode } : {}),
      }))
    }
    // Send only the config fields the admin actually touched, so an untouched
    // drawer can never rewrite a job description or an interview length.
    const changedConfig = diffConfiguration(configuration, configEdits)
    if (Object.keys(changedConfig).length) payload.configuration = changedConfig

    // Nothing changed beyond identifiers
    if (Object.keys(payload).length <= 2) {
      message.info('No changes to save')
      return
    }

    setSaving(true)
    try {
      const res = await adminRequest.put('/assessment/details', payload)
      const data = res?.data?.data || {}
      const warnings = []
      if (data.skippedRemovals?.length) {
        warnings.push(`Not removed (already attempted): ${data.skippedRemovals.map(s => s.email).filter(Boolean).join(', ')}`)
      }
      if (data.skippedNewStudents?.length) {
        warnings.push(`Not added — new institute students need a degree & department, so add them via "Add Candidate": ${data.skippedNewStudents.join(', ')}`)
      }
      if (data.addError) {
        warnings.push(`Some students could not be added: ${data.addError}`)
      }
      if (data.skippedMobileUpdates?.length) {
        warnings.push(`Mobile not updated for: ${data.skippedMobileUpdates.map(s => s.email || s.assignedId).join(', ')}`)
      }
      if (data.added?.failedAccounts?.length) {
        warnings.push(`Could not create accounts for: ${data.added.failedAccounts.map(f => f.email).filter(Boolean).join(', ')}`)
      }
      if (warnings.length) {
        message.warning(warnings.join('  •  '), 8)
      } else {
        message.success('Assessment updated')
      }
      onSuccess?.()
      onClose()
    } catch (error) {
      console.error('Error updating assessment:', error)
      message.error(error?.response?.data?.message || 'Failed to update assessment')
    } finally {
      setSaving(false)
    }
  }

  // Combined rows: existing students + staged new students. Existing rows carry the
  // stored E.164 mobile, staged rows a code + national pair — both rendered through
  // formatMobile so the column reads the same either way.
  const dataSource = [
    ...students.map(s => {
      const staged = mobileEdits[s.assignedId]
      const stored = splitFullMobile(s.mobile)
      return {
        ...s,
        _type: 'existing',
        _key: s.assignedId,
        _mobile: staged
          ? formatMobile(staged.countryCode, staged.phone)
          : (stored ? formatMobile(stored.countryCode, stored.nationalNumber) : ''),
        // Only flag the profile source while no override is staged — once the
        // admin edits the row, the number shown is this assessment's own.
        _mobileFromProfile: !staged && !!s.mobileFromProfile,
      }
    }),
    ...addStudents.map(s => ({
      email: s.email,
      firstName: s.firstName,
      lastName: s.lastName,
      _type: 'new',
      _key: `new-${s.email}`,
      _mobile: formatMobile(s.countryCode, s.phone),
    })),
  ]

  const studentColumns = [
    {
      title: 'Email',
      render: (_, record) => {
        const isRemoved = record._type === 'existing' && removeAssignedIds.includes(record.assignedId)
        return (
          <span
            style={{
              fontSize: '12px',
              wordBreak: 'break-all',
              textDecoration: isRemoved ? 'line-through' : 'none',
              color: isRemoved ? '#9ca3af' : 'inherit',
            }}
          >
            {record.email || '-'}
            {record._type === 'new' && <NewBadge>New</NewBadge>}
          </span>
        )
      },
    },
    {
      title: 'Mobile',
      width: 230,
      render: (_, record) => {
        if (record._type === 'existing' && editingMobileId === record.assignedId) {
          return (
            <InlineMobileEditor>
              <Select
                showSearch
                size="small"
                aria-label="Country dialling code"
                value={editCountryCode}
                options={countryCodeOptions}
                optionFilterProp="searchText"
                onChange={setEditCountryCode}
              />
              <Input
                size="small"
                value={editMobile}
                onChange={e => setEditMobile(e.target.value.replace(/[^\d+\-\s]/g, ''))}
                onPressEnter={() => handleSaveMobile(record.assignedId)}
                placeholder={`${nationalNumberLength(editCountryCode)} digits`}
                aria-label="Mobile number without country code"
                type="tel"
                maxLength={16}
              />
              <SaveOutlined
                role="button"
                tabIndex={0}
                aria-label="Save mobile number"
                style={{ color: '#52c41a', cursor: 'pointer', fontSize: '14px' }}
                onClick={() => handleSaveMobile(record.assignedId)}
                onKeyDown={e => e.key === 'Enter' && handleSaveMobile(record.assignedId)}
              />
              <CloseOutlined
                role="button"
                tabIndex={0}
                aria-label="Cancel mobile edit"
                style={{ color: '#ff4d4f', cursor: 'pointer', fontSize: '14px' }}
                onClick={handleCancelEditMobile}
                onKeyDown={e => e.key === 'Enter' && handleCancelEditMobile()}
              />
            </InlineMobileEditor>
          )
        }
        const isRemoved = record._type === 'existing' && removeAssignedIds.includes(record.assignedId)
        return (
          <span
            style={{
              fontSize: '12px',
              whiteSpace: 'nowrap',
              textDecoration: isRemoved ? 'line-through' : 'none',
              color: isRemoved || !record._mobile ? '#9ca3af' : 'inherit',
            }}
          >
            {record._mobile || '-'}
            {record._mobileFromProfile && record._mobile && (
              <Tooltip title="From the student's profile — editing sets a number for this assessment">
                <ProfileTag>profile</ProfileTag>
              </Tooltip>
            )}
            {record._type === 'existing' && !isRemoved && (
              <EditOutlined
                role="button"
                tabIndex={0}
                aria-label={record._mobile ? 'Edit mobile number' : 'Add mobile number'}
                style={{ color: '#1890ff', cursor: 'pointer', fontSize: '13px', marginLeft: '8px' }}
                onClick={() => handleEditMobile(record)}
                onKeyDown={e => e.key === 'Enter' && handleEditMobile(record)}
              />
            )}
          </span>
        )
      },
    },
    {
      title: 'Action',
      width: 90,
      align: 'center',
      render: (_, record) => {
        if (record._type === 'new') {
          return (
            <DeleteOutlined
              style={{ color: '#ff4d4f', cursor: 'pointer', fontSize: '14px' }}
              onClick={() => handleRemoveStagedStudent(record.email)}
            />
          )
        }
        if (record.attempted) {
          return (
            <Tooltip title="Student has already attempted">
              <DeleteOutlined style={{ color: '#d1d5db', cursor: 'not-allowed', fontSize: '14px' }} />
            </Tooltip>
          )
        }
        const isRemoved = removeAssignedIds.includes(record.assignedId)
        if (isRemoved) {
          return (
            <Button.Default
              size="small"
              text="Undo"
              icon={<UndoOutlined />}
              onClick={() => toggleRemove(record)}
            />
          )
        }
        return (
          <DeleteOutlined
            style={{ color: '#ff4d4f', cursor: 'pointer', fontSize: '14px' }}
            onClick={() => toggleRemove(record)}
          />
        )
      },
    },
  ]

  return (
    <Drawer
      title={null}
      placement="right"
      /* 640, not 560: the student table now carries an inline mobile editor
         (dial code + number + save/cancel) that cramps the email column at 560. */
      width={640}
      onClose={onClose}
      open={visible}
      closable={false}
      headerStyle={{ display: 'none' }}
      bodyStyle={{ padding: 0 }}
      footerStyle={{ padding: 0 }}
      destroyOnClose
      footer={
        <FooterContainer>
          <Button.Secondary text="Cancel" onClick={onClose} />
          <Button.Primary text="Save" onClick={handleSave} disabled={saving} />
        </FooterContainer>
      }
    >
      <Spin spinning={loading || saving}>
        {/* Header */}
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          padding: '20px 24px',
          borderBottom: '1px solid #e5e7eb',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <div style={{ fontSize: '18px', fontWeight: 600, color: '#1f2937' }}>
              Edit Assessment
            </div>
            {mapType && (
              <InfoBadge bg="#EEF2FF" color="#4338CA">
                {mapType === 'corporate' ? 'Corporate' : 'Institute'}
              </InfoBadge>
            )}
          </div>
          <CloseOutlined
            style={{ fontSize: '16px', color: '#6b7280', cursor: 'pointer' }}
            onClick={onClose}
          />
        </div>

        <DrawerContent>
          {/* Assessment Name */}
          <Section>
            <Label>Assessment Name *</Label>
            <Input
              value={name}
              onChange={e => setName(e.target.value)}
              placeholder="Enter assessment name"
              style={{ fontSize: '14px' }}
            />
          </Section>

          {/* End Date & Time */}
          <Section>
            <Label>End Date &amp; Time *</Label>
            <AntDatePicker
              value={endDate}
              onChange={date => setEndDate(date)}
              showTime={{ format: 'hh:mm A', use12Hours: true }}
              format="DD/MM/YYYY hh:mm A"
              style={{ width: '100%' }}
              placeholder="Select end date & time"
            />
          </Section>

          {/* Configuration the assessment was published with */}
          <Section>
            <AssessmentConfigurationSection
              configuration={configuration}
              edits={configEdits}
              onFieldChange={handleConfigChange}
            />
          </Section>

          {/* Student List */}
          <Section>
            <SectionTitle>
              Student List
              <StudentCount>
                ({students.length + addStudents.length} student
                {students.length + addStudents.length !== 1 ? 's' : ''})
              </StudentCount>
            </SectionTitle>

            <AddStudentForm>
              <AddStudentRow>
                <Input
                  value={newFirstName}
                  onChange={e => setNewFirstName(e.target.value)}
                  onPressEnter={handleAddStudent}
                  placeholder="First name"
                  aria-label="First name"
                  style={{ flex: 1, fontSize: '14px' }}
                />
                <Input
                  value={newLastName}
                  onChange={e => setNewLastName(e.target.value)}
                  onPressEnter={handleAddStudent}
                  placeholder="Last name (optional)"
                  aria-label="Last name"
                  style={{ flex: 1, fontSize: '14px' }}
                />
              </AddStudentRow>
              <AddStudentRow>
                <Input
                  value={newEmail}
                  onChange={e => setNewEmail(e.target.value)}
                  onPressEnter={handleAddStudent}
                  placeholder="Email"
                  aria-label="Email"
                  type="email"
                  style={{ flex: 1, fontSize: '14px' }}
                />
              </AddStudentRow>
              <AddStudentRow>
                <MobileFieldGroup>
                  <Select
                    showSearch
                    aria-label="Country dialling code"
                    value={newCountryCode}
                    options={countryCodeOptions}
                    optionFilterProp="searchText"
                    onChange={setNewCountryCode}
                  />
                  <Input
                    value={newMobile}
                    onChange={e => setNewMobile(e.target.value.replace(/[^\d+\-\s]/g, ''))}
                    onPressEnter={handleAddStudent}
                    placeholder={`Mobile — optional, ${nationalNumberLength(newCountryCode)} digits`}
                    aria-label="Mobile number without country code"
                    type="tel"
                    maxLength={16}
                    style={{ fontSize: '14px' }}
                  />
                </MobileFieldGroup>
                <Button.Primary text="Add" icon={<PlusOutlined />} onClick={handleAddStudent} />
              </AddStudentRow>
            </AddStudentForm>

            <Table
              columns={studentColumns}
              dataSource={dataSource}
              rowKey="_key"
              size="small"
              pagination={dataSource.length > 10 ? { pageSize: 10, size: 'small' } : false}
              scroll={{ y: 300 }}
              style={{ fontSize: '12px' }}
              locale={{ emptyText: 'No students in this assessment' }}
            />
          </Section>
        </DrawerContent>
      </Spin>
    </Drawer>
  )
}

export default EditAssessmentDrawer
